import { useEffect, useState, useMemo } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { getWorkersPaginated } from '../../../../redux/slices/workersSlice';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import TableViewLayout from 'layouts/TableViewLayout';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

const toActionEntity = (row: any): ActionEntity => ({
  type: 'worker',
  id: row.worker_id,
  name: row.worker_name,
  enabled: !!row.enabled,
  maintenanceModeActive: !!row.maintenance_mode_active,
});

const ListWorkers = () => {
  const dispatch = useAppDispatch();
  const { items: workers, status, loading, config: headerConfig, filtersConfig, pagination, error } = useAppSelector((state) => state.workers);

  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'workers';
  const [showSidebar, setShowSidebar] = useState(false);

  const isTableLoading = loading.getWorkersPaginated;

  useEffect(() => {
    if (status === null) {
      dispatch(getWorkersPaginated({}) as any);
    }
  }, [dispatch, status]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: getWorkersPaginated,
    columnFormats: { last_seen: 'datetime', status_timestamp: 'datetime' },
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh });
      return <ActionDropdown actions={actions} items={['addToTransportWorker', 'edit', 'delete', 'enable', 'disable', 'restart']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });
  const columns = [...baseColumns, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(workers || []), [workers]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.worker_id),
  });

  const { currentPage, totalPages, totalItems } = pagination;

  const selectionEntities = table.getSelectedRowModel().rows.map((r: any) => toActionEntity(r.original));
  const toolbarActions = getActions({ entities: selectionEntities, refresh });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={(headerConfig ?? []).map((c: any) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isTableLoading}
      isSilentRefresh={isSilentRefresh}
      toolbarButtons={
        <>
          <ActionButton actions={toolbarActions} action="createWorker" variant="primary" />
          <ActionButton actions={toolbarActions} action="addToTransportWorker" className="ms-2" variant="secondary" label="Add selected to transport" />
        </>
      }
    />
  );
};

export default ListWorkers;
